import * as Sentry from "@sentry/react";

type ErrorContext = Record<string, unknown>;

const sensitiveKeyPattern = /authorization|cookie|credential|email|password|secret|token/i;
const sentryDsn = (import.meta.env.VITE_SENTRY_DSN as string | undefined)?.trim();
const sentryEnvironment =
	(import.meta.env.VITE_SENTRY_ENVIRONMENT as string | undefined)?.trim() ||
	(import.meta.env.PROD ? "production" : "development");
const sentryRelease = (import.meta.env.VITE_SENTRY_RELEASE as string | undefined)?.trim();

if (sentryDsn) {
	Sentry.init({
		dsn: sentryDsn,
		environment: sentryEnvironment,
		release: sentryRelease || undefined,
		sendDefaultPii: false,
		tracesSampleRate: import.meta.env.PROD ? 0.1 : 0,
		tracePropagationTargets: [window.location.origin],
		beforeSend(event) {
			delete event.request;
			delete event.user;
			return event;
		},
	});
}

function sanitizeValue(value: unknown, depth = 0): unknown {
	if (depth >= 3) return "[truncated]";
	if (value === null || value === undefined) return value;
	if (typeof value === "string") return value.slice(0, 1_000);
	if (typeof value === "number" || typeof value === "boolean") return value;
	if (Array.isArray(value)) return value.slice(0, 20).map((entry) => sanitizeValue(entry, depth + 1));
	if (typeof value !== "object") return String(value).slice(0, 1_000);

	const sanitized: Record<string, unknown> = {};
	for (const [key, entry] of Object.entries(value)) {
		sanitized[key] = sensitiveKeyPattern.test(key)
			? "[redacted]"
			: sanitizeValue(entry, depth + 1);
	}
	return sanitized;
}

export function captureClientException(
	message: string,
	error?: unknown,
	context?: ErrorContext,
): void {
	if (!sentryDsn) return;
	const exception = error instanceof Error ? error : new Error(message);
	Sentry.withScope((scope) => {
		scope.setTag("source", "cms");
		scope.setExtra("message", message);
		if (context) scope.setContext("context", sanitizeValue(context) as Record<string, unknown>);
		Sentry.captureException(exception);
	});
}
